import { useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, Layers, MessageSquare, Monitor, X } from 'lucide-react'
import { activeInstance } from '../instances'
import { productName } from '../lib/config'
import { CLAUDE_URL, copyText } from './ask'
import { openHowItsBuilt } from './HowItsBuilt'
import './claude.css'

// How it works (Sam, 18 Oct 2026): the first time someone opens their Command
// Centre, one short walkthrough. Step 1 is the picture: the Command Centre is
// the name for both halves, the screens where you look and Claude where you
// get things done. Step 2 gets Claude ready in three ticks. It opens only when
// someone asks for it: the "How it works" button, or a link ending
// #how-it-works. Never by itself (Sam, 22 Oct 2026: "this pop-up is too much").
// Instances turn it on with `claude.welcome`.

const OPEN_EVENT = 'omni-claude:welcome'
const SEEN_KEY = `omni-welcome-seen:${import.meta.env.VITE_INSTANCE_SLUG ?? 'default'}`

export function openWelcome() {
  window.dispatchEvent(new Event(OPEN_EVENT))
}

function markSeen() {
  try {
    window.localStorage.setItem(SEEN_KEY, '1')
  } catch {
    /* private window: it simply shows again next time */
  }
}

export function WelcomeHost({ screens }: { screens: string[] }) {
  const enabled = activeInstance.claude?.welcome === true
  const [open, setOpen] = useState(() => {
    if (!enabled) return false
    try {
      if (window.location.hash === '#how-it-works') return true
    } catch {
      /* ignore */
    }
    return false
  })
  useEffect(() => {
    const on = () => setOpen(true)
    window.addEventListener(OPEN_EVENT, on)
    return () => window.removeEventListener(OPEN_EVENT, on)
  }, [])
  if (!enabled || !open) return null
  return (
    <Welcome
      screens={screens}
      onClose={() => {
        markSeen()
        setOpen(false)
        if (window.location.hash === '#how-it-works') history.replaceState(null, '', window.location.pathname + window.location.search)
      }}
    />
  )
}

function Welcome({ screens, onClose }: { screens: string[]; onClose: () => void }) {
  const c = activeInstance.claude!
  const [step, setStep] = useState<1 | 2>(1)
  const [done, setDone] = useState([false, false, false])
  const [copied, setCopied] = useState(false)
  const tick = (i: number) => setDone((d) => d.map((v, j) => (j === i ? true : v)))
  const count = done.filter(Boolean).length

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const shown = screens.slice(0, 4)

  return (
    <div className="oc-wel" role="dialog" aria-modal="true" aria-label={`How your ${productName} works`}>
      <div className="oc-wel-card">
        <div className="oc-wel-top">
          <span className="oc-wel-stepno">Step {step} of 2</span>
          <button type="button" className="oc-wel-x" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === 1 ? (
          <>
            <h2 className="oc-wel-h">Your {productName} has two halves.</h2>
            <p className="oc-wel-sub">The screens show you what is going on. Claude does the work. Same business, same data.</p>

            <div className="oc-wel-box">
              <p className="oc-wel-boxlabel">{productName}</p>
              <div className="oc-wel-halves">
                <div className="oc-wel-half oc-wel-screens">
                  <div className="oc-wel-halfhead">
                    <span className="oc-wel-icon">
                      <Monitor className="h-5 w-5" />
                    </span>
                    <span>
                      <b>The screens</b>
                      <small>Where you look</small>
                    </span>
                  </div>
                  <ul>
                    {shown.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                  <p className="oc-wel-note">This website, on any device.</p>
                </div>

                <div className="oc-wel-arrows" aria-hidden>
                  <span className="oc-wel-ask">
                    You ask <ArrowRight className="h-4 w-4" />
                  </span>
                  <span className="oc-wel-back">
                    <ArrowLeft className="h-4 w-4" /> It shows up here
                  </span>
                </div>

                <div className="oc-wel-half oc-wel-claude">
                  <div className="oc-wel-halfhead">
                    <span className="oc-wel-icon">
                      <MessageSquare className="h-5 w-5" />
                    </span>
                    <span>
                      <b>Claude</b>
                      <small>Where you get things done</small>
                    </span>
                  </div>
                  <p className="oc-wel-bubble oc-wel-you">{c.example.ask}</p>
                  <p className="oc-wel-bubble">{c.example.answer}</p>
                  <p className="oc-wel-note">A chat. Ask in plain words, like a colleague.</p>
                </div>
              </div>
            </div>

            <ol className="oc-wel-three">
              <li>
                <span>1</span>
                <span>
                  <b>Look</b> at the screens to see what needs doing.
                </span>
              </li>
              <li>
                <span>2</span>
                <span>
                  <b>Ask</b> Claude for it, in plain words.
                </span>
              </li>
              <li>
                <span>3</span>
                <span>
                  <b>Check</b> the result here. Nothing goes out without your yes.
                </span>
              </li>
            </ol>

            <div className="oc-wel-actions">
              <button type="button" className="oc-wel-primary" onClick={() => setStep(2)}>
                Next: get Claude ready <ArrowRight className="h-4 w-4" />
              </button>
              {c.built && (
                <button
                  type="button"
                  className="oc-wel-quiet"
                  onClick={() => {
                    onClose()
                    openHowItsBuilt()
                  }}
                >
                  <Layers className="h-4 w-4" /> See everything behind it
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <h2 className="oc-wel-h">Get Claude ready</h2>
            <p className="oc-wel-sub">Three steps. Claude is already connected to your business, so there is nothing to type in.</p>
            <div className="oc-wel-progress" aria-hidden>
              <i style={{ width: `${Math.round((count / 3) * 100)}%` }} />
            </div>

            <ol className="oc-wel-setup">
              <li className={done[0] ? 'is-done' : undefined}>
                <span className="oc-wel-tick">{done[0] ? <Check className="h-4 w-4" /> : '1'}</span>
                <span className="oc-wel-text">
                  <b>Open Claude</b>
                  <small>Sign in at claude.ai/code with your work email.</small>
                </span>
                <a className="oc-wel-btn" href={CLAUDE_URL} target="_blank" rel="noopener noreferrer" onClick={() => tick(0)}>
                  Open Claude
                </a>
              </li>
              <li className={done[1] ? 'is-done' : undefined}>
                <span className="oc-wel-tick">{done[1] ? <Check className="h-4 w-4" /> : '2'}</span>
                <span className="oc-wel-text">
                  <b>
                    Pick <code>{c.folder}</code>
                  </b>
                  <small>
                    {c.environment ? (
                      <>
                        and the <code>{c.environment}</code> environment.{' '}
                      </>
                    ) : null}
                    Both are already in your list. Pick them for every new chat.
                  </small>
                </span>
                <button type="button" className="oc-wel-btn" onClick={() => tick(1)}>
                  Done
                </button>
              </li>
              <li className={done[2] ? 'is-done' : undefined}>
                <span className="oc-wel-tick">{done[2] ? <Check className="h-4 w-4" /> : '3'}</span>
                <span className="oc-wel-text">
                  <b>Ask your first question</b>
                  <small>"{c.example.ask}"</small>
                </span>
                <button
                  type="button"
                  className="oc-wel-btn"
                  onClick={async () => {
                    setCopied(await copyText(c.example.ask))
                    tick(2)
                  }}
                >
                  {copied ? 'Copied, now paste it' : 'Copy question'}
                </button>
              </li>
            </ol>

            {count === 3 && (
              <div className="oc-wel-done" role="status">
                <b>You are set up.</b> Look here, ask Claude, check the result here. Forget? Press How it works in the menu.
              </div>
            )}

            <div className="oc-wel-actions">
              <button type="button" className="oc-wel-primary" onClick={onClose}>
                Go to my {productName}
              </button>
              <button type="button" className="oc-wel-quiet" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
